import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { ArticleBatchDto } from 'src/app/shared/dto/article-batch.dto';
import { ArticleDto } from 'src/app/shared/dto/article.dto';
import { ArticleBatchResponseDto } from 'src/app/shared/dto/pagination/article-batch-response.dto';
import { RawMaterialDto } from 'src/app/shared/dto/rawMaterial.dto';
import { BaseService } from 'src/app/shared/services/base.service';
import { SocketService } from 'src/app/shared/services/socket.service';
import { Messages } from 'src/app/shared/sockets/enums/messages';
import { Client, formatClientRoom, RoomNames } from 'src/app/shared/sockets/enums/rooms';

@Injectable({
    providedIn: 'root',
})
export class ArticleBatchesService extends BaseService {
    private articleBatchesSubject = new BehaviorSubject<ArticleBatchDto[]>([]);
    public articleBatches$ = this.articleBatchesSubject.asObservable();

    private readonly socketService = inject(SocketService);

    constructor(httpClient: HttpClient) {
        super(httpClient, 'articles');
    }

    async setupWebSocket(): Promise<void> {
        try {
            this.articleBatchesSubject.next((await this.findAllBatches()).articlesBatches);
            this.socketService.conectSocketRoom(formatClientRoom(Client.DEFAULT_CLIENT, RoomNames.ARTICLES_ROOM));
            this.socketService.getNotifications(Messages.ARTICLES_CHANGED).subscribe(async () => {
                const response = await this.findAllBatches();
                this.articleBatchesSubject.next(response.articlesBatches);
            });
        } catch (e: any) {
            throw new Error(`Error al establecer conexión con el socket de Lotes de artículos. Error: ${e.message}`);
        }
    }

    disconnectWebSocket(): void {
        this.socketService.disconnectSocketRoom(formatClientRoom(Client.DEFAULT_CLIENT, RoomNames.ARTICLE_BATCHES_ROOM));
    }

    async findAllBatches(): Promise<ArticleBatchResponseDto> {
        try {
            const request = this.httpClient.get<ArticleBatchResponseDto>(`${this.url}/findAllBatches`);

            return await lastValueFrom(request);
        } catch (e: any) {
            if (e.error.statusCode === 401) {
                throw new Error(`${e.error.message}`);
            }

            throw new Error(`No se ha encontrado ningun lote de artículos. Error: ${e.error.message}`);
        }
    }

    // TODO: REMOVE? findAll is doing the same
    async getArticleBatches(): Promise<ArticleBatchDto[]> {
        try {
            const articleBatches = this.httpClient.get<ArticleBatchDto[]>(
                `${this.url}/getAllBatches`
            );

            return await lastValueFrom(articleBatches);
        } catch (e: any) {
            throw new Error(`No se ha podido obtener los lotes: Error: ${e.error.message}`);
        }
    }

    async removeArticleBatch(articleBatchId: number): Promise<ArticleBatchDto> {
        try {
            if (articleBatchId <= 0) {
                throw new Error('El id del lote ha de ser mayor de 0');
            }

            const articleBatch = this.httpClient.delete<ArticleBatchDto>(`${this.url}/removeArticleBatch/${articleBatchId}`);

            return await lastValueFrom(articleBatch);
        } catch (e: any) {
            throw new Error(`No se ha podido eliminar el lote: Error: ${e.error.message}`);
        }
    }

    async createArticleBatch(articleBatch: ArticleBatchDto): Promise<ArticleBatchDto> {
        try {

            if (!articleBatch) {
                throw new Error('El lote no puede ser nulo');
            }

            const request = this.httpClient.post<ArticleBatchDto>(`${this.url}/createArticleBatch`, articleBatch);

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se ha podido crear el lote. Error: ${e.error.message}`);
        }
    }

    async saveSelectedRawMaterials(
        selectedArticleBatches: ArticleBatchDto[],
        orderProcessId: number
    ): Promise<void> {
        try {
            if (selectedArticleBatches.length === 0 || orderProcessId < 0) {
                throw new Error('Los parámetros para guardar las materias primas no pueden estar vacíos.');
            }

            await lastValueFrom(
                this.httpClient.post<any>(`${this.url}/saveRawMaterials`, {
                    articleBatches: selectedArticleBatches,
                    orderProcessId: orderProcessId
                })
            );
        } catch (e: any) {
            throw new Error(`No se ha podido guardar las materias primas para el proceso ${orderProcessId}: Error: ${e.error.message}`);
        }
    }

    async incrementQuantity(articleBatchId: number, quantity: number): Promise<ArticleBatchDto> {
        try {
            if (articleBatchId <= 0) {
                throw new Error('El id del lote ha de ser mayor de 0');
            }

            const request = this.httpClient.patch<ArticleBatchDto>(`${this.url}/incrementArticleBatchQuantity/${articleBatchId}`, {quantity});

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se ha podido actualizar la cantidad del lote. Error: ${e.error.message}`);
        }
    }

    /**
     * Get the batches of an article
     * @param articleId The id of the article
     * @returns Array of batches of the article
     */
    async getBatchesByArticleId(articleId: number): Promise<ArticleBatchDto[]> {
        try {
            if (articleId <= 0) {
                throw new Error('El id del articulo ha de ser mayor de 0');
            }

            const request = this.httpClient.get<ArticleBatchDto[]>(`${this.url}/getBatchesByArticleId/${articleId}`);

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se han podido obtener los lotes para el artículo ${articleId}. Error: ${e.error.message}`);
        }
    }

    async substractBatchesQuantity(rawMaterials: RawMaterialDto[], quantityToProduce: number): Promise<[ArticleDto[], ArticleBatchDto[]]> {
        try {
            if (!rawMaterials || rawMaterials.length === 0) {
                throw new Error('The raw materials array must have at least one element');
            }

            if (!quantityToProduce || quantityToProduce <= 0) {
                throw new Error('The given quantity to produce must be greater than 0');
            }

            const request = this.httpClient.post<[ArticleDto[], ArticleBatchDto[]]>(`${this.url}/substractQuantitiesByRawMaterials`, {
                rawMaterials,
                quantityToProduce
            });

            const response: [ArticleDto[], ArticleBatchDto[]] = await lastValueFrom(request);

            return response;
        } catch (e: any) {
            throw new Error(`No se han podido restar las cantidades. Error: ${e.error.message}`);
        }
    }
}
